import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { assignSummaryAction, fetchTalkByIdAction, updateTalkAction } from '../../../utils/redux/talks/talksAction';
import { Container, Row, Col, Card, Badge, Button, Form } from 'react-bootstrap';
import { BsPencilSquare, BsCheck, BsX } from 'react-icons/bs';
// import './TalkDetails.css';
import { fetchAllCategories } from '../../../utils/redux/category/categoryAction';
import '../CSS/commondetails.css'

const TalkDetails = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const { selectedTalk, isLoading } = useSelector((state) => state.talks);
    const { categories } = useSelector((state) => state.category);

    // Edit states
    const [editMode, setEditMode] = useState({
        title: false,
        basicInfo: false,
        links: false,
        description: false,
        summary: false,
        keywords: false,
        tags: false,
        category: false
    });

    // Form states
    const [formData, setFormData] = useState({});


    const [showAssign, setShowAssign] = useState(false);
    const [assignee, setAssignee] = useState({ name: '', email: '' });
    const [assignError, setAssignError] = useState('');


    const handleAssignSummary = async (e) => {
        e.preventDefault();
        setAssignError('');
        if (!assignee.name.trim() || !assignee.email.trim()) {
            setAssignError("Name and email are required");
            return;
        }
        const res = await dispatch(assignSummaryAction(id, assignee));
        if (res?.success) {
            setShowAssign(false);
            setAssignee({ name: '', email: '' });
        } else {
            setAssignError(res?.message || "Failed to assign");
        }
    };



    useEffect(() => {
        if (id) {
            dispatch(fetchTalkByIdAction(id));
        }
    }, [dispatch, id]);

    useEffect(() => {
        dispatch(fetchAllCategories());
    }, [dispatch]);

    useEffect(() => {
        if (selectedTalk) {
            setFormData(selectedTalk);
        }
    }, [selectedTalk]);

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const renderNoDataMessage = (message) => (
        <p className="no-data-message">{message}</p>
    );

    const handleEdit = (field) => {
        setEditMode({ ...editMode, [field]: true });
    };

    const handleCancel = (field) => {
        setEditMode({ ...editMode, [field]: false });
        setFormData({ ...formData, [field]: selectedTalk[field] });
    };

    // Update the handleSave function to handle array fields
    const handleSave = async (field) => {
        try {
            let valueToSave = formData[field];

            // Handle array fields (keywords and tags)
            if (field === 'keywords' || field === 'tags') {
                // Convert string input to array
                valueToSave = typeof valueToSave === 'string'
                    ? valueToSave.split(',').map(item => item.trim()).filter(Boolean)
                    : valueToSave;
            }

            if (field === 'category') {
                valueToSave = valueToSave || null;
            }

            const response = await dispatch(updateTalkAction(id, {
                [field]: valueToSave
            }));

            if (response?.success) {
                setEditMode({ ...editMode, [field]: false });
                // Update formData with processed array
                setFormData(prev => ({
                    ...prev,
                    [field]: valueToSave
                }));
            }
        } catch (error) {
            console.error('Error updating talk:', error);
        }
    };

    const EditButtons = ({ field }) => (
        <div className="edit-buttons">
            {!editMode[field] ? (
                <Button variant="link" size="sm" onClick={() => handleEdit(field)}>
                    <BsPencilSquare />
                </Button>
            ) : (
                <>
                    <Button variant="link" className="text-success" size="sm" onClick={() => handleSave(field)}>
                        <BsCheck />
                    </Button>
                    <Button variant="link" className="text-danger" size="sm" onClick={() => handleCancel(field)}>
                        <BsX />
                    </Button>
                </>
            )}
        </div>
    );

    // Update the renderEditableField function to handle large text areas
    const renderEditableField = (field, content, inputType = 'text') => (
        <div className="editable-field">
            {editMode[field] ? (
                inputType === 'textarea' ? (
                    <Form.Control
                        as="textarea"
                        rows={10}
                        className="large-textarea"
                        value={formData[field] || ''}
                        onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                    />
                ) : (
                    <Form.Control
                        type={inputType}
                        value={formData[field] || ''}
                        onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                    />
                )
            ) : (
                content
            )}
            <EditButtons field={field} />
        </div>
    );

    // Update the renderEditableArray function
    const renderEditableArray = (field, items, placeholder) => (
        <div className="editable-field">
            {editMode[field] ? (
                <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder={`Enter ${field} (comma separated)`}
                    // Check if the value is a string (being edited) or an array (initial state)
                    value={typeof formData[field] === 'string'
                        ? formData[field]
                        : (formData[field]?.join(', ') || '')}
                    onChange={(e) => {
                        setFormData({
                            ...formData,
                            [field]: e.target.value // Store as string while editing
                        });
                    }}
                    className="tags-textarea"
                />
            ) : (
                <div className="tags-container">
                    {Array.isArray(items) && items.length > 0 ? (
                        items.map((item, index) => (
                            <Badge
                                key={index}
                                bg={field === 'keywords' ? 'secondary' : 'light'}
                                text={field === 'tags' ? 'dark' : undefined}
                                className="tag-badge"
                            >
                                {item}
                            </Badge>
                        ))
                    ) : (
                        <span className="text-muted">{placeholder}</span>
                    )}
                </div>
            )}
            <EditButtons field={field} />
        </div>
    );

    const [copySuccess, setCopySuccess] = useState('');

    const handleCopyAssigneeLink = () => {
        const link = `${window.location.origin}/submit-summary/${id}`;
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(link).then(() => {
                setCopySuccess('Link copied!');
                setTimeout(() => setCopySuccess(''), 1500);
            });
        } else {
            // Fallback for HTTP or unsupported browsers
            const textarea = document.createElement('textarea');
            textarea.value = link;
            document.body.appendChild(textarea);
            textarea.select();
            try {
                document.execCommand('copy');
                setCopySuccess('Link copied!');
            } catch (err) {
                setCopySuccess('Copy failed');
            }
            document.body.removeChild(textarea);
            setTimeout(() => setCopySuccess(''), 1500);
        }
    };

    const handleCopyAssigneeMessage = () => {
        const link = `${window.location.origin}/submit-summary/${id}`;
        const message = `
Hi,
You have been assigned to submit a summary for a talk. Details are as follows:

Talk Name: ${formData?.title},

Talk Link: ${formData?.talkLink || 'Not Available'},

Submission Link: ${link}

Thank You!
    `;
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(message).then(() => {
                setCopySuccess("Message Copied to clipboard!");
                setTimeout(() => setCopySuccess(''), 1500);
            });
        } else {
            // Fallback for HTTP or unsupported browsers
            const textarea = document.createElement('textarea');
            textarea.value = message;
            document.body.appendChild(textarea);
            textarea.select();
            try {
                document.execCommand('copy');
                setCopySuccess('Message Copied to clipboard!');
            } catch (err) {
                setCopySuccess('Copy failed');
            }
            document.body.removeChild(textarea);
            setTimeout(() => setCopySuccess(''), 1500);
        }
    };

    if (isLoading || !selectedTalk) {
        return <div className="text-center mt-5">Loading...</div>;
    }

    return (
        <Container className="py-4">
            <Card className="talk-details-card">
                <Card.Body>
                    {/* Title Section */}
                    <div className="title-section mb-4">
                        {renderEditableField('title',
                            <h2>{formData.title || "No title available"}</h2>
                        )}
                    </div>

                    <Row className="mb-4">
                        {/* Basic Information Column */}
                        <Col md={6}>
                            <div className="detail-section">
                                <h5 className="section-title">Basic Information</h5>
                                <div className="info-content">
                                    {renderEditableField('talkDate',
                                        <div className="detail-item">
                                            <span className="label">Date:</span>
                                            <span>{formData.talkDate ? formatDate(formData.talkDate) : "No date specified"}</span>
                                        </div>,
                                        'date'
                                    )}
                                    {renderEditableField('durationHhmmss',
                                        <div className="detail-item">
                                            <span className="label">Duration:</span>
                                            <span>{formData.durationHhmmss || "Duration not specified"}</span>
                                        </div>
                                    )}
                                    {editMode.category ? (
                                        <div className="editable-field">
                                            <Form.Select
                                                value={formData.category?._id || formData.category || ''}
                                                onChange={e => setFormData({ ...formData, category: e.target.value })}
                                            >
                                                <option value="">Select Category</option>
                                                {categories?.map(cat => (
                                                    <option key={cat._id} value={cat._id}>{cat.name}</option>
                                                ))}
                                            </Form.Select>
                                            <EditButtons field="category" />
                                        </div>
                                    ) : (
                                        <div className="editable-field">
                                            <div className="detail-item">
                                                <span className="label">Category:</span>
                                                <span>
                                                    {formData.category?.name || categories?.find(c => c._id === formData.category)?.name || "Category not assigned"}
                                                </span>
                                            </div>
                                            <EditButtons field="category" />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Col>

                        {/* Links Column */}
                        <Col md={6}>
                            <div className="detail-section">
                                <h5 className="section-title">Links</h5>
                                <div className="info-content">
                                    {renderEditableField('talkLink',
                                        <div className="detail-item">
                                            <span className="label">Talk Link:</span>
                                            {formData.talkLink ? (
                                                <a href={formData.talkLink} target="_blank" rel="noopener noreferrer">
                                                    View Talk
                                                </a>
                                            ) : (
                                                <span className="text-muted">No talk link available</span>
                                            )}
                                        </div>
                                    )}
                                    {renderEditableField('summaryLink',
                                        <div className="detail-item">
                                            <span className="label">Summary Link:</span>
                                            {formData.summaryLink && formData.summaryExist ? (
                                                <a href={formData.summaryLink} target="_blank" rel="noopener noreferrer">
                                                    View Summary
                                                </a>
                                            ) : (
                                                <span className="text-muted">No summary link available</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Description Section */}
                    <div className="detail-section mb-4">
                        <h5 className="section-title">Description</h5>
                        <div className="content-section">
                            {renderEditableField('description',
                                <div className="content-text formatted-text">
                                    {formData.description ?
                                        formData.description.split('\n').map((paragraph, index) => (
                                            <p key={index}>{paragraph}</p>
                                        ))
                                        : "No description added yet"}
                                </div>,
                                'textarea'
                            )}
                        </div>
                    </div>

                    {/* Summary Section */}
                    <div className="detail-section mb-4">
                        <h5 className="section-title">Summary</h5>
                        <div className="content-section">
                            {renderEditableField('summary',
                                <div className="content-text summary-background formatted-text">
                                    {formData.summary ?
                                        formData.summary.split('\n').map((paragraph, index) => (
                                            <p key={index}>{paragraph}</p>
                                        ))
                                        : "No summary added yet"}
                                </div>,
                                'textarea'
                            )}
                            <div className="mt-3">
                                {formData.summaryExist ? (
                                    <div>
                                        <strong>Summary Status:</strong> <span className="text-success">Completed</span>
                                        {formData.summaryAssignment?.assignedTo?.name && (
                                            <div>
                                                <strong>By:</strong> {formData.summaryAssignment.assignedTo.name} ({formData.summaryAssignment.assignedTo.email})
                                            </div>
                                        )}
                                    </div>
                                ) : formData.summaryAssignment?.status === "ASSIGNED" ? (
                                    <div>
                                        <strong>Summary Assigned To:</strong> {formData.summaryAssignment.assignedTo?.name} ({formData.summaryAssignment.assignedTo?.email})<br />
                                        <span className="text-warning">Status: Assigned</span>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            className="ms-2"
                                            onClick={handleCopyAssigneeLink}
                                        >
                                            Copy Link for Assignee
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            className="ms-2"
                                            onClick={handleCopyAssigneeMessage}
                                        >
                                            Copy Message for Assignee
                                        </Button>
                                        {copySuccess && <span className="ms-2 text-success">{copySuccess}</span>}
                                    </div>
                                ) : (
                                    <div>
                                        <Button variant="outline-primary" size="sm" onClick={() => setShowAssign(true)}>
                                            Assign for Summary
                                        </Button>
                                        {showAssign && (
                                            <Form className="mt-3" onSubmit={handleAssignSummary}>
                                                <Row>
                                                    <Col md={5}>
                                                        <Form.Control
                                                            placeholder="Assignee Name"
                                                            value={assignee.name}
                                                            onChange={e => setAssignee({ ...assignee, name: e.target.value })}
                                                            size="sm"
                                                        />
                                                    </Col>
                                                    <Col md={5}>
                                                        <Form.Control
                                                            placeholder="Assignee Email"
                                                            value={assignee.email}
                                                            onChange={e => setAssignee({ ...assignee, email: e.target.value })}
                                                            size="sm"
                                                            type="email"
                                                        />
                                                    </Col>
                                                    <Col md={2}>
                                                        <Button type="submit" variant="success" size="sm">
                                                            Assign
                                                        </Button>
                                                    </Col>
                                                </Row>
                                                {assignError && <div className="text-danger mt-2">{assignError}</div>}
                                            </Form>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Keywords Section */}
                    <div className="detail-section mb-4">
                        <h5 className="section-title">Keywords</h5>
                        {renderEditableArray('keywords', formData.keywords, "No keywords added yet")}
                    </div>

                    {/* Tags Section */}
                    <div className="detail-section">
                        <h5 className="section-title">Tags</h5>
                        {renderEditableArray('tags', formData.tags, "No tags added yet")}
                    </div>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default TalkDetails;